export interface PhoneCountry {
  /** ISO 3166-1 alpha-2 */
  readonly iso: string;
  readonly name: string;
  /** E.164 dialing prefix including leading + */
  readonly dialCode: string;
  readonly flag: string;
}

export const DEFAULT_PHONE_COUNTRY_ISO = "US";

/** United States entry used as the picker default. */
export const DEFAULT_PHONE_COUNTRY: PhoneCountry = {
  dialCode: "+1",
  flag: "🇺🇸",
  iso: DEFAULT_PHONE_COUNTRY_ISO,
  name: "United States",
};

/** Curated country list for phone country pickers. United States is first (default). */
export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  DEFAULT_PHONE_COUNTRY,
  { dialCode: "+1", flag: "🇨🇦", iso: "CA", name: "Canada" },
  { dialCode: "+44", flag: "🇬🇧", iso: "GB", name: "United Kingdom" },
  { dialCode: "+61", flag: "🇦🇺", iso: "AU", name: "Australia" },
  { dialCode: "+49", flag: "🇩🇪", iso: "DE", name: "Germany" },
  { dialCode: "+33", flag: "🇫🇷", iso: "FR", name: "France" },
  { dialCode: "+34", flag: "🇪🇸", iso: "ES", name: "Spain" },
  { dialCode: "+39", flag: "🇮🇹", iso: "IT", name: "Italy" },
  { dialCode: "+31", flag: "🇳🇱", iso: "NL", name: "Netherlands" },
  { dialCode: "+32", flag: "🇧🇪", iso: "BE", name: "Belgium" },
  { dialCode: "+41", flag: "🇨🇭", iso: "CH", name: "Switzerland" },
  { dialCode: "+43", flag: "🇦🇹", iso: "AT", name: "Austria" },
  { dialCode: "+46", flag: "🇸🇪", iso: "SE", name: "Sweden" },
  { dialCode: "+47", flag: "🇳🇴", iso: "NO", name: "Norway" },
  { dialCode: "+45", flag: "🇩🇰", iso: "DK", name: "Denmark" },
  { dialCode: "+358", flag: "🇫🇮", iso: "FI", name: "Finland" },
  { dialCode: "+353", flag: "🇮🇪", iso: "IE", name: "Ireland" },
  { dialCode: "+351", flag: "🇵🇹", iso: "PT", name: "Portugal" },
  { dialCode: "+48", flag: "🇵🇱", iso: "PL", name: "Poland" },
  { dialCode: "+420", flag: "🇨🇿", iso: "CZ", name: "Czechia" },
  { dialCode: "+36", flag: "🇭🇺", iso: "HU", name: "Hungary" },
  { dialCode: "+30", flag: "🇬🇷", iso: "GR", name: "Greece" },
  { dialCode: "+40", flag: "🇷🇴", iso: "RO", name: "Romania" },
  { dialCode: "+7", flag: "🇷🇺", iso: "RU", name: "Russia" },
  { dialCode: "+380", flag: "🇺🇦", iso: "UA", name: "Ukraine" },
  { dialCode: "+90", flag: "🇹🇷", iso: "TR", name: "Turkey" },
  { dialCode: "+972", flag: "🇮🇱", iso: "IL", name: "Israel" },
  { dialCode: "+971", flag: "🇦🇪", iso: "AE", name: "United Arab Emirates" },
  { dialCode: "+966", flag: "🇸🇦", iso: "SA", name: "Saudi Arabia" },
  { dialCode: "+91", flag: "🇮🇳", iso: "IN", name: "India" },
  { dialCode: "+92", flag: "🇵🇰", iso: "PK", name: "Pakistan" },
  { dialCode: "+880", flag: "🇧🇩", iso: "BD", name: "Bangladesh" },
  { dialCode: "+94", flag: "🇱🇰", iso: "LK", name: "Sri Lanka" },
  { dialCode: "+977", flag: "🇳🇵", iso: "NP", name: "Nepal" },
  { dialCode: "+66", flag: "🇹🇭", iso: "TH", name: "Thailand" },
  { dialCode: "+84", flag: "🇻🇳", iso: "VN", name: "Vietnam" },
  { dialCode: "+62", flag: "🇮🇩", iso: "ID", name: "Indonesia" },
  { dialCode: "+60", flag: "🇲🇾", iso: "MY", name: "Malaysia" },
  { dialCode: "+65", flag: "🇸🇬", iso: "SG", name: "Singapore" },
  { dialCode: "+63", flag: "🇵🇭", iso: "PH", name: "Philippines" },
  { dialCode: "+81", flag: "🇯🇵", iso: "JP", name: "Japan" },
  { dialCode: "+82", flag: "🇰🇷", iso: "KR", name: "South Korea" },
  { dialCode: "+86", flag: "🇨🇳", iso: "CN", name: "China" },
  { dialCode: "+852", flag: "🇭🇰", iso: "HK", name: "Hong Kong" },
  { dialCode: "+853", flag: "🇲🇴", iso: "MO", name: "Macau" },
  { dialCode: "+886", flag: "🇹🇼", iso: "TW", name: "Taiwan" },
  { dialCode: "+64", flag: "🇳🇿", iso: "NZ", name: "New Zealand" },
  { dialCode: "+27", flag: "🇿🇦", iso: "ZA", name: "South Africa" },
  { dialCode: "+234", flag: "🇳🇬", iso: "NG", name: "Nigeria" },
  { dialCode: "+254", flag: "🇰🇪", iso: "KE", name: "Kenya" },
  { dialCode: "+233", flag: "🇬🇭", iso: "GH", name: "Ghana" },
  { dialCode: "+20", flag: "🇪🇬", iso: "EG", name: "Egypt" },
  { dialCode: "+212", flag: "🇲🇦", iso: "MA", name: "Morocco" },
  { dialCode: "+55", flag: "🇧🇷", iso: "BR", name: "Brazil" },
  { dialCode: "+54", flag: "🇦🇷", iso: "AR", name: "Argentina" },
  { dialCode: "+56", flag: "🇨🇱", iso: "CL", name: "Chile" },
  { dialCode: "+57", flag: "🇨🇴", iso: "CO", name: "Colombia" },
  { dialCode: "+52", flag: "🇲🇽", iso: "MX", name: "Mexico" },
  { dialCode: "+51", flag: "🇵🇪", iso: "PE", name: "Peru" },
  { dialCode: "+58", flag: "🇻🇪", iso: "VE", name: "Venezuela" },
  { dialCode: "+593", flag: "🇪🇨", iso: "EC", name: "Ecuador" },
  { dialCode: "+506", flag: "🇨🇷", iso: "CR", name: "Costa Rica" },
  { dialCode: "+507", flag: "🇵🇦", iso: "PA", name: "Panama" },
  { dialCode: "+502", flag: "🇬🇹", iso: "GT", name: "Guatemala" },
  { dialCode: "+503", flag: "🇸🇻", iso: "SV", name: "El Salvador" },
  { dialCode: "+504", flag: "🇭🇳", iso: "HN", name: "Honduras" },
  { dialCode: "+505", flag: "🇳🇮", iso: "NI", name: "Nicaragua" },
  { dialCode: "+1", flag: "🇵🇷", iso: "PR", name: "Puerto Rico" },
  { dialCode: "+1", flag: "🇯🇲", iso: "JM", name: "Jamaica" },
  { dialCode: "+1", flag: "🇹🇹", iso: "TT", name: "Trinidad and Tobago" },
  { dialCode: "+1", flag: "🇧🇸", iso: "BS", name: "Bahamas" },
  { dialCode: "+1", flag: "🇧🇧", iso: "BB", name: "Barbados" },
];

const byIso = new Map(PHONE_COUNTRIES.map((c) => [c.iso, c]));

export function getPhoneCountry(iso: string | undefined | null): PhoneCountry {
  if (!iso) {
    return DEFAULT_PHONE_COUNTRY;
  }
  return byIso.get(iso) ?? DEFAULT_PHONE_COUNTRY;
}

export function isUsPhoneCountry(iso: string | undefined | null): boolean {
  return getPhoneCountry(iso).iso === DEFAULT_PHONE_COUNTRY_ISO;
}

/** Map API `countryCode` (+1) / stored values to an ISO for the picker. */
export function resolvePhoneCountryIsoFromDialCode(dialCode?: string | null): string {
  if (!dialCode) {
    return DEFAULT_PHONE_COUNTRY_ISO;
  }
  const normalized = dialCode.startsWith("+") ? dialCode : `+${dialCode}`;
  if (normalized === "+1") {
    return DEFAULT_PHONE_COUNTRY_ISO;
  }
  const match = PHONE_COUNTRIES.find((c) => c.dialCode === normalized);
  return match?.iso ?? DEFAULT_PHONE_COUNTRY_ISO;
}

export function formatCountryOptionLabel(country: PhoneCountry): string {
  return `${country.flag} ${country.name} (${country.dialCode})`;
}
